import { useToast as useToastification } from 'vue-toastification';
import type { PluginOptions } from 'vue-toastification';

// Toast configuration matching the design system
export const toastConfig: PluginOptions = {
  transition: 'Vue-Toastification__bounce',
  maxToasts: 3,
  newestOnTop: true,
  position: 'top-right',
  timeout: 5000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: 'button',
  icon: true,
  rtl: false,
};

// Custom toast service
class ToastService {
  private toast: ReturnType<typeof useToastification> | null = null;

  init() {
    this.toast = useToastification();
  }

  success(message: string, title?: string) {
    const content = title ? `${title}: ${message}` : message;
    this.toast?.success(content, {
      timeout: 4000,
    });
  }

  error(message: string, title?: string) {
    const content = title ? `${title}: ${message}` : message;
    this.toast?.error(content, {
      timeout: 6000,
    });
  }

  warning(message: string, title?: string) {
    const content = title ? `${title}: ${message}` : message;
    this.toast?.warning(content, {
      timeout: 5000,
    });
  }

  info(message: string, title?: string) {
    const content = title ? `${title}: ${message}` : message;
    this.toast?.info(content, {
      timeout: 5000,
    });
  }

  loading(message: string) {
    return this.toast?.info(message, {
      timeout: false,
      closeOnClick: false,
      closeButton: false,
      icon: '⏳',
    });
  }

  clear() {
    this.toast?.clear();
  }

  // Replace native alert
  alert(message: string) {
    this.info(message);
  }

  // Replace native confirm with promise-based confirmation
  async confirm(message: string, title = 'Confirmação'): Promise<boolean> {
    // For confirmation dialogs, use native confirm with better formatting
    // Vue Toastification doesn't handle interactive HTML well without custom components
    return window.confirm(`${title}\n\n${message}`);
  }
}

// Export singleton instance
export const toast = new ToastService();

// Export composable for Vue components
export function useToast() {
  if (!toast.toast) {
    toast.init();
  }
  return toast;
}
